import { JSX, useMemo, useCallback } from "react";

import { List, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocationsDataQuery } from "src/api/locationQueries.api";
import { useSidebarExpandStore } from "src/store/sidebar.store";
import LocationType from "src/types/LocationType";

import { useGroupedLocations } from "../hooks/useGroupedLocations";
import { getDestinationRouteFromLocationType, getListItemTextFromLocationType, getIconFromLocationType } from "../sidebar.helpers";
import { RegionGroup } from "./RegionGroup";
import SectionHeader from "./SectionHeader";

export type Props = {
  locationType: LocationType;
  open: boolean;
  parentEnabled: boolean;
  childsEnabled: boolean;
};

function SidebarLocationTypeList({ open, locationType, parentEnabled, childsEnabled }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: locations } = useLocationsDataQuery();
  const { expand, setExpand, setLocationId, setRegionExpand } = useSidebarExpandStore();

  const list = useMemo(() => locations?.value ?? [], [locations]);

  const { grouped, isSingleRegion } = useGroupedLocations(list, locationType);

  const isExpanded = expand[locationType]?.expand ?? false;

  const canNavigate = grouped.length === 1 && !grouped.every((gr) => gr.region.isVirtualRegion === true);

  const showToggle = open && list.length > 0;

  const onHeaderClick = useCallback(() => {
    if (!canNavigate) return;
    const only = grouped[0];
    if (!only) return;
    if (locationType === LocationType.Mall) navigate(`/center-region-dashboard/${only.region.id}`);
    if (locationType === LocationType.Shop) navigate(`/shop-region-dashboard/${only.region.id}`);
    if (locationType === LocationType.Station) navigate(`/station-region-dashboard/${only.region.id}`);
  }, [canNavigate, grouped, locationType, navigate]);

  const onToggleExpand = useCallback(() => setExpand(locationType, !isExpanded), [setExpand, locationType, isExpanded]);

  const onSelectLocation = useCallback(
    (id: string) => {
      setLocationId(locationType, id);
      navigate(getDestinationRouteFromLocationType(locationType, id));
    },
    [navigate, setLocationId, locationType]
  );

  const onNavigateRegion = useCallback(
    (regionId: string) => {
      if (locationType === LocationType.Mall) navigate(`/center-region-dashboard/${regionId}`);
      if (locationType === LocationType.Shop) navigate(`/shop-region-dashboard/${regionId}`);
      if (locationType === LocationType.Station) navigate(`/station-region-dashboard/${regionId}`);
    },
    [locationType, navigate]
  );

  if (!parentEnabled && !childsEnabled) return null;

  return (
    <List>
      <SectionHeader
        open={open}
        canNavigate={canNavigate}
        isExpanded={isExpanded}
        toggleExpand={onToggleExpand}
        label={t(getListItemTextFromLocationType(locationType))}
        onClick={onHeaderClick}
        icon={getIconFromLocationType(locationType)}
        showToggle={showToggle}
      />

      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        {grouped.map(({ region, children }) => {
          const regionExpandState = expand[locationType]?.regions?.[region.label]?.expand ?? false;
          return (
            <RegionGroup
              key={region.label}
              open={open}
              locationType={locationType}
              region={region}
              children={children}
              pathname={pathname}
              isVirtualRegion={region.isVirtualRegion}
              isSingleRegion={isSingleRegion}
              regionExpanded={regionExpandState}
              onToggleRegion={() => setRegionExpand(locationType, region.label, !regionExpandState)}
              onNavigateRegion={onNavigateRegion}
              onSelectLocation={onSelectLocation}
            />
          );
        })}
      </Collapse>
    </List>
  );
}

export default SidebarLocationTypeList;
